import {
    BigmlDatasetDTO,
    BigmlDatasetsDTO,
    BigmlEnvioDTO,
    BigmlFieldDTO,
    BigmlFieldsDTO,
    BigmlFilesDTO,
    BigmlModelDTO,
    BigmlProyectoDTO,
    RespuestasGptDTO,
    BlackBoxRespuestaDTO,
    IBigmlDatasetDTO,
    IBigmlModelDTO,
    IBigmlEnvioDTO,
    IBigmlFieldDTO,
    IBigmlFieldsDTO,
    IBigmlFilesDTO,
    IBigmlProyectoDTO,
    INSELevelsDTO,
    NSELevelsDTO,
    IBlackBoxRespuestaDTO,
    IComparatorRespuestaDTO,
    ComparatorRespuestaDTO
} from "@DTOS";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Constants } from "@constants";
import { map } from "rxjs/operators";
import { firstValueFrom } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class BigmlServiceBackend {

    constructor(private httpClient: HttpClient) { }

    public async getProyectos(): Promise<BigmlProyectoDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IBigmlProyectoDTO[]>(`${Constants.apiURL}/bigml-projects`).pipe(
                map(response => response.map(proyecto => new BigmlProyectoDTO(proyecto)))
            )
        ) ?? [];
    }

    public async getProyecto(id: number): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IBigmlProyectoDTO>(
                `${Constants.apiURL}/bigml-projects/${id}`
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async getProjectsCount(): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IBigmlProyectoDTO>(
                `${Constants.apiURL}/bigml/get_projects_count`
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async deleteProyecto(id: number): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.delete<IBigmlProyectoDTO>(
                `${Constants.apiURL}/bigml-projects/${id}`
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async deleteProyectos(bigmlFiles: BigmlFilesDTO): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml-projects/destroy_multiple`, bigmlFiles.toJSON()
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async getAllDatasets(projectId: number): Promise<BigmlDatasetDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IBigmlDatasetDTO[]>(`${Constants.apiURL}/bigml-projects/${projectId}/datasets`).pipe(
                map(response => response.map(dataset => new BigmlDatasetDTO(dataset)))
            )
        ) ?? [];
    }

    public async getDatasets(id: number): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml-projects/${id}/get_datasets`
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async updatePredictDatasetWithFile(bigmlEnvio: BigmlEnvioDTO): Promise<BigmlDatasetDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlDatasetDTO>(
                `${Constants.apiURL}/bigml-datasets/update_predict_dataset_with_file`,
                bigmlEnvio.toJSON()
            )
        );

        return new BigmlDatasetDTO(response);
    }

    public async createInitialProject(bigmlEnvio: BigmlEnvioDTO): Promise<BigmlEnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlEnvioDTO>(
                `${Constants.apiURL}/bigml-projects`, bigmlEnvio.toJSON()
            )
        );

        return new BigmlEnvioDTO(response);
    }

    public async removeKillers(bigmlEnvio: BigmlEnvioDTO): Promise<BigmlEnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient
                .post<IBigmlEnvioDTO>(`${Constants.apiURL}/bigml-datasets/remove_killers`, bigmlEnvio.toJSON())
        );

        return new BigmlEnvioDTO(response);
    }

    public async applyFilters(projectId: number, bigmlEnvio: BigmlEnvioDTO): Promise<BigmlEnvioDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlEnvioDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/apply_filters`, bigmlEnvio.toJSON()
            )
        );

        return new BigmlEnvioDTO(response);
    }

    public async generateFiles(projectId: number, bigmlFiles: BigmlFilesDTO): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/create_final_files`, bigmlFiles.toJSON()
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async createBigmlProject(projectId: number): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlProyectoDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/create_in_bigml`, null
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async createWithBases(bigmlEnvio: BigmlEnvioDTO): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-projects/create_with_bases`, bigmlEnvio.toJSON()
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async updateProject(project: BigmlProyectoDTO): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-projects/${project.id}`, project.toJSON()
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async updateFields(fields: BigmlFieldsDTO): Promise<BigmlFieldsDTO> {
        const response = await firstValueFrom(
            this.httpClient.put<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-fields`, fields.toJSON()
            )
        );

        return new BigmlFieldsDTO(response);
    }

    public async updateAnswersGpt(respuestas: RespuestasGptDTO): Promise<void> {
        await firstValueFrom(
            this.httpClient.put<IBigmlFieldsDTO>(
                `${Constants.apiURL}/gpt-answers`, respuestas.toJSON()
            )
        );
    }

    public async prepareFields(bigmlFields: BigmlFieldsDTO): Promise<BigmlFieldsDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-fields/prepare`, bigmlFields.toJSON()
            )
        );

        return new BigmlFieldsDTO(response);
    }

    public async resetForTrain(projectId: number): Promise<BigmlFieldsDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/reset_for_train`, null
            )
        );

        return new BigmlFieldsDTO(response);
    }

    public async getModels(projectId: number): Promise<BigmlModelDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IBigmlModelDTO[]>(
                `${Constants.apiURL}/bigml-projects/${projectId}/models`
            ).pipe(
                map(response => response.map(model => new BigmlModelDTO(model)))
            )
        ) ?? [];
    }

    public async resetSentToPredict(projectId: number): Promise<BigmlModelDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlModelDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/models/reset_sent_to_predict`, {}
            )
        );

        return new BigmlModelDTO(response);
    }

    public async getFields(projectId: number): Promise<BigmlFieldDTO[]> {
        return await firstValueFrom(
            this.httpClient.get<IBigmlFieldDTO[]>(
                `${Constants.apiURL}/bigml-projects/${projectId}/fields`
            ).pipe(
                map(response => response.map(field => new BigmlFieldDTO(field)))
            )
        ) ?? [];
    }

    public async prepareModels(bigmlFields: BigmlFieldsDTO): Promise<BigmlFieldsDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-models/prepare`, bigmlFields.toJSON()
            )
        );

        return new BigmlFieldsDTO(response);
    }

    public async needsDatasetCreation(projectId: number): Promise<BigmlProyectoDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlProyectoDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/needs_dataset_creation`, null
            )
        );

        return new BigmlProyectoDTO(response);
    }

    public async cleanTextFields(projectId: number, fields: BigmlFieldsDTO): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldsDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/clean_text_fields`, fields.toJSON()
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async getLessFrequentAnswers(fieldId: number): Promise<BigmlFieldDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFieldDTO>(
                `${Constants.apiURL}/bigml-fields/${fieldId}/get_less_frequent_answers`, null
            )
        );

        return new BigmlFieldDTO(response);
    }

    public async removeEmptyAnswers(projectId: number, bigmlFiles: BigmlFilesDTO): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/clean_dataset`,
                bigmlFiles.toJSON()
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async removeSpecificAnswers(projectId: number, bigmlFiles: BigmlFilesDTO): Promise<BigmlDatasetsDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml-projects/${projectId}/remove_specific_answers`,
                bigmlFiles.toJSON()
            )
        );

        return new BigmlDatasetsDTO(response);
    }

    public async weightDataset(bigmlFiles: BigmlFilesDTO): Promise<BigmlFilesDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBigmlFilesDTO>(
                `${Constants.apiURL}/bigml/weight_dataset`, bigmlFiles.toJSON()
            )
        );

        return new BigmlFilesDTO(response);
    }

    public async getMergedDataset(bigmlFiles: BigmlFilesDTO): Promise<Blob> {
        return await firstValueFrom(
            this.httpClient.post(
                `${Constants.apiURL}/bigml-datasets/merge_train_predicted`, bigmlFiles.toJSON(), { responseType: "arraybuffer" }
            ).pipe(map(data => new Blob([data], { type: "text/csv;charset=utf-8" })))
        );
    }

    public async getDatasetFile(bigmlFiles: BigmlFilesDTO): Promise<Blob> {
        return await firstValueFrom(
            this.httpClient.post(
                `${Constants.apiURL}/bigml-datasets/create_file`, bigmlFiles.toJSON(), { responseType: "arraybuffer" }
            ).pipe(map(data => new Blob([data], { type: "text/csv;charset=utf-8" })))
        );
    }

    public async getNSELevels(): Promise<NSELevelsDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<INSELevelsDTO>(
                `${Constants.apiURL}/users/nse/get_levels`
            )
        );

        return new NSELevelsDTO(response);
    }

    public async evaluateWithBlackBox(formdata: FormData): Promise<BlackBoxRespuestaDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IBlackBoxRespuestaDTO>(
                `${Constants.apiURL}/black-box`, formdata
            )
        );

        return new BlackBoxRespuestaDTO(response);
    }

    public async compareDatasets(formdata: FormData): Promise<ComparatorRespuestaDTO> {
        const response = await firstValueFrom(
            this.httpClient.post<IComparatorRespuestaDTO>(
                `${Constants.apiURL}/pyramid-comparator`, formdata
            )
        );

        return new ComparatorRespuestaDTO(response);
    }

}
